import { useEffect, useRef, useState } from "react"
import { cn } from "../../lib/utils"
import type { TranscriptChunk } from "../../contexts/EncounterContext"
import { Stethoscope, User, Users } from "lucide-react"

interface TranscriptPanelProps {
  chunks: TranscriptChunk[]
  highlightRange?: { start: number; end: number }
  onChunkClick?: (chunk: TranscriptChunk) => void
  autoScroll?: boolean
}

const SPEAKER_CONFIG = {
  clinician: { color: "text-blue-500", bg: "bg-blue-500/10", label: "Clinician", icon: Stethoscope },
  patient: { color: "text-green-500", bg: "bg-green-500/10", label: "Patient", icon: User },
  other: { color: "text-gray-400", bg: "bg-gray-400/10", label: "Other", icon: Users },
} as const

const formatMs = (ms: number): string => {
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
}

const isHighlighted = (
  chunk: TranscriptChunk,
  range?: { start: number; end: number }
): boolean => {
  if (!range) return false
  if (chunk.charStart < 0 || chunk.charEnd < 0) return false
  return chunk.charStart < range.end && chunk.charEnd > range.start
}

const MAX_VISIBLE_CHUNKS = 100

const TranscriptPanel = ({
  chunks,
  highlightRange,
  onChunkClick,
  autoScroll = true,
}: TranscriptPanelProps) => {
  const bottomRef = useRef<HTMLDivElement>(null)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    if (autoScroll) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" })
    }
  }, [chunks.length, autoScroll])

  if (chunks.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground p-6">
        Transcript will appear here as the encounter is recorded.
      </div>
    )
  }

  const isTruncated = !showAll && chunks.length > MAX_VISIBLE_CHUNKS
  const visibleChunks = isTruncated ? chunks.slice(-MAX_VISIBLE_CHUNKS) : chunks

  return (
    <div className="flex flex-col gap-1 p-3 overflow-y-auto h-full">
      {isTruncated && (
        <button
          onClick={() => setShowAll(true)}
          className="text-xs text-blue-500 hover:text-blue-600 py-2 text-center"
        >
          Show earlier ({chunks.length - MAX_VISIBLE_CHUNKS} more)
        </button>
      )}
      {visibleChunks.map((chunk) => {
        const config = SPEAKER_CONFIG[chunk.speaker]
        const Icon = config.icon
        const highlighted = isHighlighted(chunk, highlightRange)

        return (
          <button
            key={chunk.chunkIndex}
            onClick={() => onChunkClick?.(chunk)}
            className={cn(
              "flex gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
              onChunkClick && "hover:bg-muted cursor-pointer",
              !onChunkClick && "cursor-default",
              highlighted && "bg-yellow-100 dark:bg-yellow-900/30"
            )}
          >
            <div className="flex flex-col items-center gap-0.5 pt-0.5 shrink-0 w-16">
              <Icon className={cn("h-3.5 w-3.5", config.color)} />
              <span className="text-[10px] text-muted-foreground">{formatMs(chunk.startMs)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <span className={cn("text-xs font-medium", config.color)}>{config.label}</span>
              <p className="text-sm leading-relaxed">{chunk.text}</p>
            </div>
          </button>
        )
      })}
      <div ref={bottomRef} />
    </div>
  )
}

export default TranscriptPanel
